/**
 * The bank: monthly interest on a child's Save bucket.
 *
 * This is the mechanic that gives a child's own money somewhere better to go
 * than the dollar store (CODING_PLAN §15). It is deliberately small — a rate,
 * an optional cap, one entry a month — because the point is that a child can
 * predict it. A number they cannot predict does not change what they do with
 * their money.
 *
 * Four decisions are load-bearing:
 *
 * 1. **Interest is only ever paid on `save`.** Not on `spend`, not on `give`,
 *    and not on `unallocated`. The whole lesson is that choosing to put money
 *    in Save is what earns; paying interest on a balance the child was about
 *    to spend anyway rewards nothing and teaches nothing. It also keeps the
 *    parent's liability legible: what the bonus costs is a function of one
 *    bucket, not of every dollar the child hasn't got round to spending.
 *    `project(...).save` is the only base, and `savings.test.js` guards it.
 *
 * 2. **Priced on the balance at the *start* of the period.** A deposit on the
 *    28th does not earn a month's interest. The alternatives are worse in both
 *    directions: end-of-month balance pays a full month on money that was
 *    there for three days, and average-daily-balance is correct but cannot be
 *    explained to a nine-year-old or checked by a parent with a calculator.
 *    Start-of-period is the version a child can verify, and it errs towards
 *    the family rather than towards the child, which is the safe direction for
 *    a liability a real parent has to actually pay.
 *
 * 3. **The rate is snapshotted onto every entry.** A parent who drops the rate
 *    from 12% to 5% has changed next month, not last March. Without the
 *    snapshot, history becomes unreadable the first time a rule changes:
 *    the ledger says $1.20 and the current rule says it should have been $0.50,
 *    and nobody can tell whether that is a bug or a memory.
 *
 * 4. **Periods are derived, never stored.** Same rule as `allowance.js` and
 *    `envelope.js`: the entry id is a pure function of (child, rule version,
 *    period index), so a job that runs late, twice, or not for three months
 *    produces exactly the rows three monthly runs would have. There is no
 *    `last_accrued_at` anywhere, because a stored cursor is a second source of
 *    truth that can disagree with the ledger.
 *
 * Compounding falls out of (2) and (4) together: each period's base is the
 * projection of everything dated before its start, and interest already
 * generated in this same call is part of that history. So one catch-up run
 * over three months and three separate monthly runs produce identical rows.
 *
 * Naming: this is a **bonus**, not "interest", in anything a younger child
 * reads, and the app says who funds it (CLAUDE.md, "lead with match, not
 * interest"). `interest` is the canonical domain term; the vocabulary on the
 * screen is display-only and cannot change a financial value.
 */

import { periodIndexAt, periodsBetween, periodStart } from './periods.js';
import { project } from './ledger.js';

export const SAVINGS_RULE_VERSION = 'fortunaut-savings-v1';

/** Monthly and only monthly. A rate quoted annually and paid weekly is a trap. */
export const SAVINGS_CADENCE = 'monthly';

export const SAVINGS_DEFAULTS = Object.freeze({
  version: SAVINGS_RULE_VERSION,
  /**
   * Off by default. A family that hasn't chosen a rate has not agreed to a
   * liability, and defaulting one on would mint money a parent never promised.
   */
  annualRatePct: 0,
  /**
   * A ceiling on what one month can pay, or null for none. Interest is
   * unbounded and compounding, and a parent who set 10% on a $40 balance has
   * not thought about what it means at $900. The cap is how they bound it
   * without having to.
   */
  monthlyCapCents: null,
});

export const withSavingsDefaults = (rule = {}) => ({ ...SAVINGS_DEFAULTS, ...rule });

export const interestEntryId = (childId, rule, periodIndex) =>
  `interest:${childId}:${withSavingsDefaults(rule).version}:${periodIndex}`;

/**
 * What one period pays on one base, with the cap applied.
 *
 * Split out so a preview screen ("at this rate you'd earn about…") uses the
 * arithmetic that actually issues, rather than a second copy of it.
 */
export function monthlyInterestCents(saveBalanceCents, rule = SAVINGS_DEFAULTS) {
  const cfg = withSavingsDefaults(rule);
  const rate = Number(cfg.annualRatePct) || 0;
  if (rate <= 0 || saveBalanceCents <= 0) return 0;

  const raw = Math.round((saveBalanceCents * rate) / 100 / 12);
  return cfg.monthlyCapCents != null ? Math.min(raw, cfg.monthlyCapCents) : raw;
}

/**
 * Every interest entry a child is owed up to `toDate` that isn't already there.
 *
 * A period earns when its start is **strictly after** `rule.effectiveFrom`. The
 * period `effectiveFrom` falls inside has already partly elapsed under no rule
 * at all, so paying it a full month would be paying for time before the parent
 * agreed to anything — and paying it a partial month would need a daily rate,
 * which is the average-daily-balance machinery decision (2) exists to avoid.
 *
 * `entries` is the child's whole ledger, existing interest included. It does
 * two jobs: the ids in it are what makes a re-run a no-op, and the amounts in
 * it are the base every period is priced on.
 */
export function dueInterestEntries({ childId, rule = SAVINGS_DEFAULTS, entries = [], toDate }) {
  const cfg = withSavingsDefaults(rule);
  const effectiveFrom = cfg.effectiveFrom;
  if (!effectiveFrom || !toDate) return [];
  if (periodIndexAt(toDate, SAVINGS_CADENCE) < periodIndexAt(effectiveFrom, SAVINGS_CADENCE)) return [];

  const seen = new Set(entries.map((e) => e.id));
  // The pool the base is projected from. Interest generated in this call joins
  // it, which is the whole of compounding: month 2 is priced on month 1's
  // balance *plus* month 1's interest, exactly as if the job had run monthly.
  const pool = [...entries];
  const created = [];

  for (const periodIndex of periodsBetween(effectiveFrom, toDate, SAVINGS_CADENCE)) {
    const start = periodStart(periodIndex, SAVINGS_CADENCE);
    if (!(start > effectiveFrom)) continue;

    const id = interestEntryId(childId, cfg, periodIndex);
    // Already issued. Skipped, not recomputed — an existing entry is history,
    // and it is already in `pool`, so it still compounds.
    if (seen.has(id)) continue;

    // Everything dated before this period began. String comparison is safe:
    // these are zero-padded YYYY-MM-DD calendar dates, never timestamps.
    const before = pool.filter((e) => String(e.occurredAt) < start);
    const saveBalanceCents = project(before, { childId }).save;
    const amountCents = monthlyInterestCents(saveBalanceCents, cfg);
    // Nothing saved, no rate, or a rounded-to-zero pittance: no entry. A row
    // that moves $0.00 is noise in a history a child is meant to read.
    if (amountCents <= 0) continue;

    const entry = {
      id,
      childId,
      currency: 'usd',
      // Interest lands back in Save. It is not unallocated income to be
      // re-split: the money that earned it is already where the child put it,
      // and sending the bonus to the allocation screen would let a child move
      // the reward for saving straight into Spend.
      bucket: 'save',
      amountCents,
      reasonType: 'interest',
      reasonId: `${cfg.version}:${periodIndex}`,
      periodIndex,
      occurredAt: start,
      snapshot: {
        annualRatePct: cfg.annualRatePct,
        monthlyCapCents: cfg.monthlyCapCents ?? null,
        saveBalanceCents,
        ruleVersion: cfg.version,
      },
    };

    seen.add(id);
    pool.push(entry);
    created.push(entry);
  }

  return created;
}

/**
 * The live rule for a child: the latest by `effectiveFrom`, `createdAt` breaking
 * a same-day tie.
 *
 * The same ordering the job and the client both use, in one place, so "which
 * rate applies" cannot be answered differently on the two sides of the wire.
 */
export function currentSavingsRule(rules = []) {
  const sorted = [...rules].sort((a, b) => (
    String(a.effectiveFrom).localeCompare(String(b.effectiveFrom))
    || String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''))
  ));
  return sorted.length ? sorted[sorted.length - 1] : null;
}
